package com.kinoshazam.uiKit.webView

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.SideEffect
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import kotlin.js.undefined
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLIFrameElement
import org.w3c.dom.HTMLSpanElement
import org.w3c.dom.HTMLStyleElement
import org.w3c.dom.MessageEvent
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import presentation.screens.webPlayer.WebPlayerMVIAction

private const val ACCENT = "#4DA3FF"
private const val CONTROLS_HIDE_MS = 3000
private const val APP_POLL_MS = 250
private const val APP_POLL_MAX_MS = 45_000
private const val SEEK_STEP_SEC = 10.0

private class WebPlayerHooks {
    var resumeSeconds: Double = -1.0
    var audio: String? = null
    var quality: String? = null
    var subtitles: String? = null
    var choiceReady: Boolean = false
    var onPlaybackChoice: (String?, String?, String?) -> Unit = { _, _, _ -> }
    var onProgress: (Double, Double, Boolean) -> Unit = { _, _, _ -> }
}

/**
 * Canvas-based Compose for Web cannot host a native WebView widget.
 * Overlay a full-screen iframe + DOM chrome (back / FS / play / seek) above the canvas.
 *
 * Progress: listen to tobaco `viewProgress` postMessage + optional same-origin bridge.
 * Focus: explicit D-pad / keyboard focus ring (needed later for Android TV too).
 */
@Composable
actual fun UiKitWebView(
    url: String,
    onAction: (WebPlayerMVIAction) -> Unit,
    title: String,
    resumePositionSec: Double,
    resumeAudio: String?,
    resumeQuality: String?,
    resumeSubtitles: String?,
    playbackChoiceReady: Boolean,
    onPlaybackChoice: (audio: String?, quality: String?, subtitles: String?) -> Unit,
    onProgress: (positionSec: Double, durationSec: Double, paused: Boolean) -> Unit,
) {
    val rootId = remember { "kinoshka-player-root" }
    val latestOnAction by rememberUpdatedState(onAction)
    val hooks = remember { WebPlayerHooks() }
    SideEffect {
        hooks.resumeSeconds = resumePositionSec
        hooks.audio = resumeAudio
        hooks.quality = resumeQuality
        hooks.subtitles = resumeSubtitles
        hooks.choiceReady = playbackChoiceReady
        hooks.onPlaybackChoice = onPlaybackChoice
        hooks.onProgress = onProgress
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black),
    )

    DisposableEffect(url) {
        val body = document.body
        var cleaningUp = false
        var hideTimerId: Int? = null
        var appPollTimerId: Int? = null
        var progressTimerId: Int? = null
        var wakeLock: dynamic = null
        var playerApp: dynamic = null
        var durationSec = 0.0
        var currentSec = 0.0
        var isPlaying = false
        var lastToggleAtMs = 0.0
        var seeking = false
        var bridgeReady = false
        var controlsVisible = true
        var focusedIndex = 1 // play by default in focusables order
        var resumeApplied = false
        var tryResume: () -> Unit = {}

        fun acquireWakeLock() {
            try {
                val nav = window.navigator.asDynamic()
                val wl = nav.wakeLock
                if (wl != null && jsTypeOf(wl.request) == "function") {
                    wl.request("screen").then { sentinel ->
                        if (cleaningUp) {
                            try {
                                sentinel.release()
                            } catch (_: Throwable) {
                            }
                        } else {
                            wakeLock = sentinel
                        }
                    }.catch { _: dynamic -> }
                }
            } catch (_: Throwable) {
            }
        }

        fun releaseWakeLock() {
            try {
                wakeLock?.release()
            } catch (_: Throwable) {
            }
            wakeLock = null
        }

        acquireWakeLock()
        val visibilityHandler: (Event) -> Unit = {
            try {
                if (document.asDynamic().visibilityState == "visible") {
                    acquireWakeLock()
                }
            } catch (_: Throwable) {
            }
        }
        document.addEventListener("visibilitychange", visibilityHandler)

        fun closePlayer() {
            if (cleaningUp) return
            latestOnAction(WebPlayerMVIAction.OnBackPressed)
        }

        fun isFullscreen(): Boolean {
            val doc = document.asDynamic()
            return doc.fullscreenElement != null ||
                doc.webkitFullscreenElement != null ||
                doc.mozFullScreenElement != null ||
                doc.msFullscreenElement != null
        }

        fun exitFullscreen() {
            val doc = document.asDynamic()
            when {
                doc.exitFullscreen != null -> doc.exitFullscreen()
                doc.webkitExitFullscreen != null -> doc.webkitExitFullscreen()
                doc.mozCancelFullScreen != null -> doc.mozCancelFullScreen()
                doc.msExitFullscreen != null -> doc.msExitFullscreen()
            }
        }

        fun requestFullscreen(el: HTMLElement) {
            val dyn = el.asDynamic()
            when {
                dyn.requestFullscreen != null -> dyn.requestFullscreen()
                dyn.webkitRequestFullscreen != null -> dyn.webkitRequestFullscreen()
                dyn.mozRequestFullScreen != null -> dyn.mozRequestFullScreen()
                dyn.msRequestFullscreen != null -> dyn.msRequestFullscreen()
            }
        }

        fun formatTime(sec: Double): String {
            if (!sec.isFinite() || sec < 0) return "0:00"
            val total = sec.toInt()
            val h = total / 3600
            val m = (total % 3600) / 60
            val s = total % 60
            val mm = if (h > 0) m.toString().padStart(2, '0') else m.toString()
            val ss = s.toString().padStart(2, '0')
            return if (h > 0) "$h:$mm:$ss" else "$mm:$ss"
        }

        // Ensure hover/focus styles stay up to date across deploys.
        val style = (document.getElementById("kinoshka-player-focus-css") as? HTMLStyleElement)
            ?: (document.createElement("style") as HTMLStyleElement).also {
                it.id = "kinoshka-player-focus-css"
                document.head?.appendChild(it)
            }
        style.textContent = FOCUS_CSS

        val root = (document.getElementById(rootId) as? HTMLDivElement)
            ?: (document.createElement("div") as HTMLDivElement).also { it.id = rootId }
        // Always re-append so the overlay sits above the Compose canvas in DOM order.
        body?.appendChild(root)
        root.style.apply {
            position = "fixed"
            left = "0"
            top = "0"
            width = "100%"
            height = "100%"
            zIndex = "2147483646"
            background = "#000"
            cssSet("transform", "translateZ(0)")
            cssSet("isolation", "isolate")
        }
        root.tabIndex = -1

        val iframe = (document.getElementById("kinoshka-player-iframe") as? HTMLIFrameElement)
            ?: (document.createElement("iframe") as HTMLIFrameElement).also {
                it.id = "kinoshka-player-iframe"
                root.appendChild(it)
            }
        val tobacoSrc = if (url.contains("?")) {
            "$url&theme=0&episodesOpen=false&showPreNext=false&showMenu=false&sharing=false"
        } else {
            "$url?theme=0&episodesOpen=false&showPreNext=false&showMenu=false&sharing=false"
        }
        // Same-origin iframe URL: webpack proxy locally, SW-backed /tobaco-embed on prod.
        val src = toSameOriginPlayerUrl(tobacoSrc)
        iframe.removeAttribute("srcdoc")
        iframe.removeAttribute("src")
        iframe.setAttribute("allow", "autoplay; encrypted-media; fullscreen")
        iframe.setAttribute("allowfullscreen", "true")
        iframe.tabIndex = -1
        iframe.style.apply {
            position = "absolute"
            left = "0"
            top = "0"
            width = "100%"
            height = "100%"
            border = "0"
            background = "#000"
            zIndex = "0"
            cssSet("pointer-events", "none")
        }

        val shield = (document.getElementById("kinoshka-player-shield") as? HTMLDivElement)
            ?: (document.createElement("div") as HTMLDivElement).also {
                it.id = "kinoshka-player-shield"
                root.appendChild(it)
            }
        shield.style.apply {
            position = "absolute"
            left = "0"
            top = "0"
            width = "100%"
            height = "100%"
            zIndex = "1"
            background = "transparent"
            cssSet("pointer-events", "auto")
        }

        // TV-style chrome: scrim + center play/title + bottom progress (Android TV parity).
        val chrome = (document.getElementById("kinoshka-player-chrome") as? HTMLDivElement)
            ?: (document.createElement("div") as HTMLDivElement).also {
                it.id = "kinoshka-player-chrome"
                root.appendChild(it)
            }
        while (chrome.firstChild != null) chrome.removeChild(chrome.firstChild!!)
        chrome.style.apply {
            position = "absolute"
            left = "0"
            top = "0"
            width = "100%"
            height = "100%"
            zIndex = "2"
            transition = "opacity 200ms ease"
            opacity = "1"
            cssSet("pointer-events", "none")
        }

        val scrim = document.createElement("div") as HTMLDivElement
        scrim.id = "kinoshka-player-scrim"
        scrim.style.apply {
            position = "absolute"
            left = "0"
            top = "0"
            width = "100%"
            height = "100%"
            background = "rgba(0,0,0,0.35)"
            cssSet("pointer-events", "none")
        }
        chrome.appendChild(scrim)

        fun styleCornerButton(btn: HTMLButtonElement) {
            btn.type = "button"
            btn.className = "kinoshka-focusable kinoshka-corner"
            btn.tabIndex = 0
            btn.style.apply {
                width = "48px"
                height = "48px"
                border = "2px solid transparent"
                borderRadius = "24px"
                background = "rgba(0,0,0,0.35)"
                color = "#fff"
                cursor = "pointer"
                display = "flex"
                cssSet("align-items", "center")
                cssSet("justify-content", "center")
                padding = "0"
                cssSet("pointer-events", "auto")
                outline = "none"
            }
        }

        val backBtn = document.createElement("button") as HTMLButtonElement
        styleCornerButton(backBtn)
        backBtn.id = "kinoshka-player-back"
        backBtn.setAttribute("aria-label", "Back")
        backBtn.innerHTML = BACK_ICON_SVG
        backBtn.onclick = { closePlayer() }
        backBtn.style.apply {
            position = "absolute"
            left = "16px"
            top = "16px"
        }
        chrome.appendChild(backBtn)

        val fsBtn = document.createElement("button") as HTMLButtonElement
        styleCornerButton(fsBtn)
        fsBtn.id = "kinoshka-player-fs"
        fsBtn.style.apply {
            position = "absolute"
            right = "16px"
            top = "16px"
        }
        chrome.appendChild(fsBtn)

        val settingsBtn = document.createElement("button") as HTMLButtonElement
        styleCornerButton(settingsBtn)
        settingsBtn.id = "kinoshka-player-settings"
        settingsBtn.setAttribute("aria-label", "Settings")
        settingsBtn.innerHTML = SETTINGS_ICON_SVG
        settingsBtn.style.apply {
            position = "absolute"
            right = "72px"
            top = "16px"
        }
        chrome.appendChild(settingsBtn)

        val settingsPanel = document.createElement("div") as HTMLDivElement
        settingsPanel.id = "kinoshka-player-settings-panel"
        settingsPanel.style.apply {
            position = "absolute"
            right = "16px"
            top = "72px"
            width = "min(320px, calc(100vw - 32px))"
            maxHeight = "min(70vh, 480px)"
            cssSet("overflow-y", "auto")
            background = "rgba(7, 16, 28, 0.94)"
            border = "1px solid rgba(255,255,255,0.12)"
            borderRadius = "12px"
            padding = "8px 0"
            boxSizing = "border-box"
            zIndex = "5"
            display = "none"
            cssSet("pointer-events", "auto")
            cssSet("backdrop-filter", "blur(10px)")
            cssSet("box-shadow", "0 12px 40px rgba(0,0,0,0.45)")
            fontFamily = "ui-sans-serif, system-ui, sans-serif"
            color = "#fff"
        }
        chrome.appendChild(settingsPanel)

        var settingsOpen = false
        var settingsView = "root" // root | quality | sound | cc | skip
        var settingsSnapshot: dynamic = null

        fun updateFullscreenIcon() {
            val full = isFullscreen()
            fsBtn.innerHTML = if (full) EXIT_FULLSCREEN_SVG else ENTER_FULLSCREEN_SVG
            fsBtn.setAttribute("aria-label", if (full) "Exit fullscreen" else "Fullscreen")
        }
        updateFullscreenIcon()
        fsBtn.onclick = {
            if (isFullscreen()) exitFullscreen() else requestFullscreen(root)
        }

        val centerCol = document.createElement("div") as HTMLDivElement
        centerCol.id = "kinoshka-player-center"
        centerCol.style.apply {
            position = "absolute"
            left = "50%"
            top = "50%"
            cssSet("transform", "translate(-50%, -50%)")
            display = "flex"
            cssSet("flex-direction", "column")
            cssSet("align-items", "center")
            cssSet("pointer-events", "none")
            maxWidth = "min(900px, 92vw)"
            width = "100%"
            boxSizing = "border-box"
            padding = "0 48px"
        }
        chrome.appendChild(centerCol)

        val titleEl = document.createElement("div") as HTMLDivElement
        titleEl.id = "kinoshka-player-title"
        titleEl.textContent = title
        titleEl.style.apply {
            color = "#fff"
            fontSize = "22px"
            fontWeight = "500"
            fontFamily = "ui-sans-serif, system-ui, sans-serif"
            textAlign = "center"
            marginBottom = if (title.isNotBlank()) "20px" else "0"
            cssSet("pointer-events", "none")
            cssSet("user-select", "none")
            cssSet("display", "-webkit-box")
            cssSet("-webkit-line-clamp", "2")
            cssSet("-webkit-box-orient", "vertical")
            cssSet("overflow", "hidden")
        }
        if (title.isBlank()) titleEl.style.display = "none"
        centerCol.appendChild(titleEl)

        val playBtn = document.createElement("button") as HTMLButtonElement
        playBtn.type = "button"
        playBtn.id = "kinoshka-player-play"
        playBtn.className = "kinoshka-focusable kinoshka-play"
        playBtn.tabIndex = 0
        playBtn.setAttribute("aria-label", "Play")
        playBtn.title = "Waiting for player bridge…"
        playBtn.innerHTML = PLAY_ICON_SVG
        playBtn.style.apply {
            width = "88px"
            height = "88px"
            border = "2px solid #fff"
            borderRadius = "44px"
            background = "rgba(0,0,0,0.45)"
            color = "#fff"
            cursor = "pointer"
            display = "flex"
            cssSet("align-items", "center")
            cssSet("justify-content", "center")
            padding = "0"
            cssSet("pointer-events", "auto")
            outline = "none"
            transition = "background 120ms ease, border-color 120ms ease, transform 120ms ease"
        }
        centerCol.appendChild(playBtn)

        val bottomBar = document.createElement("div") as HTMLDivElement
        bottomBar.id = "kinoshka-player-transport"
        bottomBar.style.apply {
            position = "absolute"
            left = "0"
            right = "0"
            bottom = "0"
            // Lift progress off the bottom edge (safe-area + breathing room).
            padding = "20px 24px calc(36px + env(safe-area-inset-bottom, 0px))"
            boxSizing = "border-box"
            cssSet("pointer-events", "none")
        }
        chrome.appendChild(bottomBar)

        val timesRow = document.createElement("div") as HTMLDivElement
        timesRow.style.apply {
            display = "flex"
            cssSet("justify-content", "space-between")
            marginBottom = "8px"
            cssSet("pointer-events", "none")
        }
        bottomBar.appendChild(timesRow)

        val timeCur = document.createElement("span") as HTMLSpanElement
        timeCur.id = "kinoshka-player-time-cur"
        timeCur.textContent = "0:00"
        timeCur.style.apply {
            color = "#fff"
            fontSize = "14px"
            fontFamily = "ui-sans-serif, system-ui, sans-serif"
            cssSet("user-select", "none")
        }
        timesRow.appendChild(timeCur)

        val timeDur = document.createElement("span") as HTMLSpanElement
        timeDur.id = "kinoshka-player-time-dur"
        timeDur.textContent = "0:00"
        timeDur.style.apply {
            color = "#fff"
            fontSize = "14px"
            fontFamily = "ui-sans-serif, system-ui, sans-serif"
            cssSet("user-select", "none")
        }
        timesRow.appendChild(timeDur)

        val seek = document.createElement("div") as HTMLDivElement
        seek.id = "kinoshka-player-seek"
        seek.className = "kinoshka-focusable kinoshka-seek"
        seek.tabIndex = 0
        seek.setAttribute("role", "slider")
        seek.setAttribute("aria-label", "Seek")
        seek.setAttribute("aria-valuemin", "0")
        seek.setAttribute("aria-valuemax", "0")
        seek.setAttribute("aria-valuenow", "0")
        seek.style.apply {
            width = "100%"
            height = "22px"
            display = "flex"
            cssSet("align-items", "center")
            cursor = "pointer"
            cssSet("pointer-events", "auto")
            outline = "none"
            boxSizing = "border-box"
            border = "2px solid transparent"
            borderRadius = "12px"
            padding = "4px 2px"
        }
        bottomBar.appendChild(seek)

        val seekTrack = document.createElement("div") as HTMLDivElement
        seekTrack.id = "kinoshka-player-seek-track"
        seekTrack.style.apply {
            position = "relative"
            width = "100%"
            height = "3px"
            borderRadius = "2px"
            background = "rgba(255,255,255,0.7)"
            cssSet("overflow", "hidden")
            transition = "height 120ms ease"
        }
        seek.appendChild(seekTrack)

        val seekFill = document.createElement("div") as HTMLDivElement
        seekFill.id = "kinoshka-player-seek-fill"
        seekFill.style.apply {
            position = "absolute"
            left = "0"
            top = "0"
            bottom = "0"
            width = "0%"
            background = ACCENT
        }
        seekTrack.appendChild(seekFill)

        // Focus order: back → play → seek → settings → fullscreen
        val focusables: List<HTMLElement> = listOf(backBtn, playBtn, seek, settingsBtn, fsBtn)
        var seekFraction = 0.0
        /** True only while pointer is over an interactive control (not the whole screen). */
        var pointerOverControl = false
        var dragSeeking = false

        fun chromeHasFocus(): Boolean {
            val active = document.activeElement ?: return false
            return focusables.any { it === active }
        }

        fun updatePlayIcon() {
            playBtn.innerHTML = if (isPlaying) PAUSE_ICON_SVG else PLAY_ICON_SVG
            playBtn.setAttribute("aria-label", if (isPlaying) "Pause" else "Play")
        }

        var seekLockTimerId: Int? = null

        fun paintSeekUi() {
            timeCur.textContent = formatTime(currentSec)
            timeDur.textContent = formatTime(durationSec)
            seekFraction = if (durationSec > 0) {
                (currentSec / durationSec).coerceIn(0.0, 1.0)
            } else {
                0.0
            }
            seekFill.style.width = "${(seekFraction * 100.0)}%"
            seek.setAttribute("aria-valuemax", durationSec.toInt().toString())
            seek.setAttribute("aria-valuenow", currentSec.toInt().toString())
        }

        fun updateSeekUi() {
            if (seeking) return
            paintSeekUi()
        }

        fun applyProgress(time: Double, duration: Double, paused: Boolean? = null) {
            if (seeking) {
                // Still learn duration while user scrubs.
                if (duration.isFinite() && duration > 0) durationSec = duration
                return
            }
            if (duration.isFinite() && duration > 0) durationSec = duration
            if (time.isFinite() && time >= 0) currentSec = time
            if (paused != null) {
                isPlaying = !paused
                updatePlayIcon()
            }
            updateSeekUi()
            hooks.onProgress(currentSec, durationSec, paused ?: !isPlaying)
            tryResume()
        }

        fun evalInPlayer(source: String): Boolean {
            return try {
                val doc = iframe.contentDocument ?: return false
                val script = doc.createElement("script")
                script.asDynamic().text = source
                (doc.head ?: doc.documentElement)?.appendChild(script)
                script.parentNode?.removeChild(script)
                true
            } catch (_: Throwable) {
                false
            }
        }

        var choiceInstalled = false
        var choiceLoopStarted = false

        fun tickPlaybackChoice() {
            if (cleaningUp) return
            if (hooks.choiceReady && !choiceInstalled && canAccessIframe(iframe)) {
                val installed = evalInPlayer(
                    PlayerEmbedScripts.installPlaybackChoiceJs(
                        audio = hooks.audio,
                        quality = hooks.quality,
                        subtitles = hooks.subtitles,
                    )
                )
                if (installed) choiceInstalled = true
            }
            try {
                val take = iframe.contentWindow?.asDynamic()?.__ksChoiceTake
                if (jsTypeOf(take) == "function") {
                    val choice = take()
                    val present = js("choice != null && choice !== undefined").unsafeCast<Boolean>()
                    if (present) {
                        hooks.onPlaybackChoice(
                            jsString(choice.audio)?.takeIf { it.isNotBlank() },
                            jsString(choice.quality)?.takeIf { it.isNotBlank() },
                            jsString(choice.subtitles)?.takeIf { it.isNotBlank() },
                        )
                    }
                }
            } catch (_: Throwable) {
            }
            window.setTimeout({ tickPlaybackChoice() }, 400)
        }

        fun startPlaybackChoice() {
            if (choiceLoopStarted) return
            choiceLoopStarted = true
            tickPlaybackChoice()
        }

        fun postToPlayer(cmd: String, extra: dynamic = null) {
            try {
                val payload = js("{}")
                payload.source = "kinoshka"
                payload.cmd = cmd
                if (extra != null) {
                    val keys = js("Object.keys")(extra).unsafeCast<Array<String>>()
                    for (k in keys) {
                        payload[k] = extra[k]
                    }
                }
                iframe.contentWindow?.postMessage(payload, "*")
            } catch (_: Throwable) {
            }
        }

        fun iframeVideo(): dynamic {
            return try {
                val win = iframe.contentWindow?.asDynamic()
                val app = playerApp ?: win?.app ?: win?.__kinoshka?.app
                app?.videoElement ?: win?.document?.querySelector("#player video")
                    ?: win?.document?.querySelector("video")
            } catch (_: Throwable) {
                null
            }
        }

        fun togglePlay() {
            val now = try {
                window.performance.now()
            } catch (_: Throwable) {
                js("Date.now()") as Double
            }
            if (now - lastToggleAtMs < 350) return
            lastToggleAtMs = now

            val video = iframeVideo()
            val wantPlay = video == null || video.paused == true
            // Drive <video> first, in the same user-gesture tick. Do not re-inject
            // scripts here — that used to drop the gesture and stack play/pause.
            if (video != null) {
                try {
                    if (wantPlay) {
                        video.play()
                        fun kickFrame() {
                            try {
                                val w = jsDouble(video.videoWidth) ?: 0.0
                                if (w > 0) return
                                val t = jsDouble(video.currentTime) ?: 0.0
                                forceVideoCurrentTime(video, if (t < 0.05) 0.05 else t)
                            } catch (_: Throwable) {
                            }
                        }
                        window.setTimeout({ kickFrame() }, 200)
                        window.setTimeout({ kickFrame() }, 800)
                    } else {
                        video.pause()
                    }
                } catch (t: Throwable) {
                    consoleInfo("kinoshka-player: video.play/pause failed: $t")
                }
            }
            try {
                val win = iframe.contentWindow?.asDynamic()
                val kn = win?.__kinoshka
                if (kn != null && jsTypeOf(kn.setPlaying) == "function") {
                    kn.setPlaying(wantPlay)
                } else if (video == null) {
                    val app = playerApp ?: win?.app
                    if (app != null) {
                        if (wantPlay) app.play() else app.pause()
                    } else {
                        postToPlayer(if (wantPlay) "play" else "pause")
                    }
                }
            } catch (t: Throwable) {
                consoleInfo("kinoshka-player: setPlaying failed: $t")
            }
            isPlaying = wantPlay
            updatePlayIcon()
        }

        /**
         * Push absolute playback position (seconds) into the Venom player.
         * Must run same-origin (dev proxy /tobaco-embed). Forces video.currentTime —
         * Venom's app.time setter no-ops when video.src is empty (MSE/HLS).
         */
        fun seekToSeconds(seconds: Double) {
            injectBridgeIntoIframe(iframe)
            val win = try {
                iframe.contentWindow?.asDynamic()
            } catch (_: Throwable) {
                null
            }
            val app = playerApp ?: try {
                win?.app ?: win?.__kinoshka?.app
            } catch (_: Throwable) {
                null
            }
            if (app != null) playerApp = app

            val video = try {
                app?.videoElement ?: win?.document?.querySelector("video")
            } catch (_: Throwable) {
                null
            }

            val videoDur = jsDouble(video?.duration) ?: jsDouble(app?.duration)
            if (videoDur != null && videoDur > 0 && videoDur.isFinite()) {
                durationSec = videoDur
            }
            val max = durationSec
            val target = if (max > 0) seconds.coerceIn(0.0, max) else seconds.coerceAtLeast(0.0)
            currentSec = target
            paintSeekUi()

            seeking = true
            seekLockTimerId?.let { window.clearTimeout(it) }
            seekLockTimerId = window.setTimeout({ seeking = false }, 1000)

            consoleInfo(
                "kinoshka-player: seek → ${target}s " +
                    "(dur=$durationSec app=${app != null} video=${video != null} " +
                    "originOk=${canAccessIframe(iframe)})"
            )

            var delivered = false

            try {
                val seekFn = win?.__kinoshka?.seek
                if (jsTypeOf(seekFn) == "function") {
                    seekFn(target)
                    delivered = true
                }
            } catch (t: Throwable) {
                consoleInfo("kinoshka-player: __kinoshka.seek error: $t")
            }

            if (!delivered && video != null) {
                try {
                    forceVideoCurrentTime(video, target)
                    delivered = true
                } catch (t: Throwable) {
                    consoleInfo("kinoshka-player: video.currentTime error: $t")
                }
            }

            if (!delivered) {
                val extra = js("{}")
                extra.time = target
                postToPlayer("seek", extra)
                consoleInfo("kinoshka-player: SEEK via postMessage — originOk=${canAccessIframe(iframe)}")
            }
        }

        fun seekToFraction(fraction: Double) {
            val f = fraction.coerceIn(0.0, 1.0)
            val base = if (durationSec > 0) durationSec else {
                try {
                    jsDouble(playerApp?.videoElement?.duration) ?: 0.0
                } catch (_: Throwable) {
                    0.0
                }
            }
            if (base <= 0) {
                consoleInfo("kinoshka-player: seek skipped — unknown duration")
                return
            }
            seekToSeconds(base * f)
        }

        fun seekBy(deltaSec: Double) {
            val base = if (durationSec > 0) durationSec else {
                try {
                    jsDouble(playerApp?.videoElement?.duration) ?: 0.0
                } catch (_: Throwable) {
                    0.0
                }
            }
            if (base <= 0) return
            durationSec = base
            seekToSeconds((currentSec + deltaSec).coerceIn(0.0, base))
        }

        tryResume = resume@{
            if (resumeApplied || seeking) return@resume
            val target = hooks.resumeSeconds
            if (target < 0 || durationSec <= 0) return@resume
            resumeApplied = true
            if (target >= 5.0 && target < durationSec * 0.9) {
                seekToSeconds(target)
            }
        }

        fun readAppProgress() {
            val app = playerApp ?: return
            try {
                val video = app.videoElement
                if (video != null) {
                    val t = jsDouble(video.currentTime)
                    val d = jsDouble(video.duration)
                    val paused = video.paused == true
                    if (d != null && d > 0 && t != null) applyProgress(t, d, paused)
                } else {
                    // Some Venom builds expose time on the app itself.
                    val t = jsDouble(app.time) ?: jsDouble(app.currentTime)
                    val d = jsDouble(app.duration)
                    if (d != null && d > 0 && t != null) applyProgress(t, d, null)
                }
            } catch (_: Throwable) {
            }
        }

        fun setBridgeReady(app: dynamic?) {
            bridgeReady = true
            if (app != null) playerApp = app
            playBtn.title = "Play / Pause"
            playBtn.style.opacity = "1"
            consoleInfo("kinoshka-player: bridge ready (app=${app != null})")
            readAppProgress()
        }

        fun tryBindApp(): Boolean {
            try {
                val win = iframe.contentWindow ?: return false
                val dyn = win.asDynamic()
                val app = dyn.app
                if (app != null && app.play != null) {
                    setBridgeReady(app)
                    return true
                }
                if (dyn.__kinoshka != null && dyn.__kinoshka.app != null) {
                    setBridgeReady(dyn.__kinoshka.app)
                    return true
                }
            } catch (_: Throwable) {
            }
            return false
        }

        fun startAppPolling() {
            val started = window.performance.now()
            fun tick() {
                if (cleaningUp || (bridgeReady && playerApp != null)) return
                if (tryBindApp()) return
                if (window.performance.now() - started > APP_POLL_MAX_MS) {
                    consoleInfo("kinoshka-player: app poll timeout")
                    if (!bridgeReady) {
                        playBtn.title = "Bridge limited — try play via postMessage"
                        playBtn.style.opacity = "0.85"
                    }
                    return
                }
                appPollTimerId = window.setTimeout({ tick() }, APP_POLL_MS)
            }
            tick()
        }

        fun startProgressPolling() {
            progressTimerId?.let { window.clearInterval(it) }
            progressTimerId = window.setInterval({
                if (!cleaningUp && !seeking) readAppProgress()
            }, 350)
        }

        fun defaultSettingsSnapshot(): dynamic {
            return js(
                "({ quality: { list: [], current: null }, sound: { list: [], current: null }, cc: { list: [], current: null }, skip: { list: [], current: null } })"
            )
        }

        fun isNullish(value: dynamic): Boolean {
            return js("value === null || value === undefined").unsafeCast<Boolean>()
        }

        fun dynGet(obj: dynamic, key: String): dynamic {
            return js("obj == null ? null : obj[key]")
        }

        fun normalizeSettingsSnapshot(raw: dynamic): dynamic {
            val out = defaultSettingsSnapshot()
            if (isNullish(raw)) return out
            try {
                fun copySection(key: String) {
                    val src = dynGet(raw, key)
                    if (isNullish(src)) return
                    val dest = dynGet(out, key)
                    try {
                        dest.list = js(
                            "Array.isArray(src.list) ? src.list.slice() : (src.list && typeof src.list.length === 'number' ? Array.prototype.slice.call(src.list) : [])"
                        )
                    } catch (_: Throwable) {
                        dest.list = js("[]")
                    }
                    try {
                        dest.current = js("src.current === undefined ? null : src.current")
                    } catch (_: Throwable) {
                        dest.current = null
                    }
                }
                copySection("quality")
                copySection("sound")
                copySection("cc")
                copySection("skip")
            } catch (t: Throwable) {
                consoleInfo("kinoshka-player: normalizeSettingsSnapshot failed: $t")
            }
            return out
        }

        fun readSettingsSnapshot(): dynamic {
            try {
                injectBridgeIntoIframe(iframe)
                val kn = iframe.contentWindow?.asDynamic()?.__kinoshka
                val fn = kn?.getSettings
                if (jsTypeOf(fn) == "function") {
                    val snap = fn()
                    if (!isNullish(snap)) return normalizeSettingsSnapshot(snap)
                }
            } catch (t: Throwable) {
                consoleInfo("kinoshka-player: getSettings failed: $t")
            }
            return defaultSettingsSnapshot()
        }

        fun applyPlayerSetting(kind: String, value: dynamic): Boolean {
            try {
                val kn = iframe.contentWindow?.asDynamic()?.__kinoshka
                val fn = kn?.setSetting
                if (jsTypeOf(fn) == "function") {
                    val ok = fn(kind, value)
                    return ok == true
                }
            } catch (_: Throwable) {
            }
            val extra = js("{}")
            extra.kind = kind
            extra.value = value
            postToPlayer("setSetting", extra)
            return false
        }

        fun rawSettingValue(value: dynamic): String? {
            if (isNullish(value)) return null
            val text = js(
                "(function(v){ if (v && typeof v === 'object') return String(v.title || v.name || v.label || v.type || ''); return String(v); })(value)"
            ).unsafeCast<String>()
            if (text.isBlank() || text == "null" || text == "undefined" || text == "-") return null
            return text
        }

        fun formatSettingLabel(kind: String, value: dynamic): String {
            if (isNullish(value)) return "—"
            return try {
                when (kind) {
                    "quality" -> {
                        val n = jsDouble(value)
                        if (n != null && n >= 100) "${n.toInt()}p"
                        else js("String(value)").unsafeCast<String>()
                    }
                    "skip" -> {
                        val title = dynGet(value, "title")
                        val type = dynGet(value, "type")
                        jsString(title) ?: jsString(type) ?: js("String(value)").unsafeCast<String>()
                    }
                    else -> js("String(value)").unsafeCast<String>()
                }
            } catch (_: Throwable) {
                "—"
            }
        }

        fun sectionTitle(kind: String): String = when (kind) {
            "quality" -> "Качество"
            "sound" -> "Озвучка"
            "cc" -> "Субтитры"
            "skip" -> "Пропустить"
            else -> kind
        }

        fun closeSettingsMenu() {
            settingsOpen = false
            settingsView = "root"
            settingsPanel.style.display = "none"
            while (settingsPanel.firstChild != null) {
                settingsPanel.removeChild(settingsPanel.firstChild!!)
            }
        }

        lateinit var renderSettingsMenu: () -> Unit

        fun styleSettingsRow(el: HTMLElement, selected: Boolean = false) {
            el.style.display = "flex"
            el.style.cssSet("align-items", "center")
            el.style.cssSet("justify-content", "space-between")
            el.style.cssSet("gap", "12px")
            el.style.width = "100%"
            el.style.boxSizing = "border-box"
            el.style.padding = "12px 16px"
            el.style.border = "0"
            el.style.background = if (selected) "rgba(77,163,255,0.22)" else "transparent"
            el.style.color = "#fff"
            el.style.fontSize = "14px"
            el.style.textAlign = "left"
            el.style.cursor = "pointer"
            el.style.cssSet("pointer-events", "auto")
            el.onmouseenter = {
                if (!selected) el.style.background = "rgba(255,255,255,0.08)"
            }
            el.onmouseleave = {
                el.style.background = if (selected) "rgba(77,163,255,0.22)" else "transparent"
            }
        }

        fun appendSettingsHeader(title: String, showBack: Boolean) {
            if (showBack) {
                val back = document.createElement("button") as HTMLButtonElement
                back.type = "button"
                back.textContent = "← $title"
                styleSettingsRow(back)
                back.style.fontWeight = "600"
                back.style.borderBottom = "1px solid rgba(255,255,255,0.08)"
                back.onclick = {
                    settingsView = "root"
                    renderSettingsMenu()
                }
                settingsPanel.appendChild(back)
            } else {
                val head = document.createElement("div") as HTMLDivElement
                head.textContent = title
                head.style.padding = "10px 16px 8px"
                head.style.fontSize = "13px"
                head.style.fontWeight = "600"
                head.style.color = "rgba(255,255,255,0.7)"
                head.style.cssSet("letter-spacing", "0.02em")
                head.style.cssSet("text-transform", "uppercase")
                settingsPanel.appendChild(head)
            }
        }

        fun sectionOf(snap: dynamic, kind: String): dynamic {
            if (isNullish(snap)) return null
            return dynGet(snap, kind)
        }

        fun appendRootRow(kind: String, section: dynamic, always: Boolean = false) {
            if (isNullish(section)) return
            val list = dynGet(section, "list")
            val len = jsArrayLength(list)
            if (len <= 0 && !always) return
            val row = document.createElement("button") as HTMLButtonElement
            row.type = "button"
            val left = document.createElement("span") as HTMLSpanElement
            left.textContent = sectionTitle(kind)
            val right = document.createElement("span") as HTMLSpanElement
            right.textContent = formatSettingLabel(kind, dynGet(section, "current"))
            right.style.color = "rgba(255,255,255,0.65)"
            right.style.fontSize = "13px"
            styleSettingsRow(row)
            row.appendChild(left)
            row.appendChild(right)
            row.onclick = {
                settingsSnapshot = readSettingsSnapshot()
                settingsView = kind
                renderSettingsMenu()
            }
            settingsPanel.appendChild(row)
        }

        fun appendOptionRows(kind: String, section: dynamic) {
            val list = if (isNullish(section)) null else dynGet(section, "list")
            val len = jsArrayLength(list)
            if (len <= 0) {
                val empty = document.createElement("div") as HTMLDivElement
                empty.textContent = "Пока нет вариантов"
                empty.style.padding = "16px"
                empty.style.color = "rgba(255,255,255,0.55)"
                empty.style.fontSize = "14px"
                settingsPanel.appendChild(empty)
                return
            }
            val current = dynGet(section, "current")
            var i = 0
            while (i < len) {
                val item = jsArrayItem(list, i)
                val row = document.createElement("button") as HTMLButtonElement
                row.type = "button"
                val label = formatSettingLabel(kind, item)
                val selected = when (kind) {
                    "skip" -> false
                    else -> try {
                        val sameStr = !isNullish(item) && !isNullish(current) &&
                            js("String(item) === String(current)").unsafeCast<Boolean>()
                        val nItem = jsDouble(item)
                        val nCur = jsDouble(current)
                        sameStr || (nItem != null && nCur != null && nItem == nCur)
                    } catch (_: Throwable) {
                        false
                    }
                }
                row.textContent = if (selected) "✓  $label" else label
                styleSettingsRow(row, selected)
                val captured = item
                row.onclick = {
                    applyPlayerSetting(kind, captured)
                    try {
                        evalInPlayer(
                            "try{window.__ksChoiceHold=window.__ksChoiceHold||{};" +
                                "window.__ksChoiceHold['$kind']=1;" +
                                "if(window.__ksChoiceUser)window.__ksChoiceUser('$kind');}catch(e){}"
                        )
                    } catch (_: Throwable) {
                    }
                    val picked = rawSettingValue(captured)
                    when (kind) {
                        "sound" -> hooks.onPlaybackChoice(picked, null, null)
                        "quality" -> hooks.onPlaybackChoice(null, picked, null)
                        "cc" -> hooks.onPlaybackChoice(null, null, picked)
                    }
                    window.setTimeout({
                        settingsSnapshot = readSettingsSnapshot()
                        if (kind == "skip") {
                            renderSettingsMenu()
                        } else {
                            settingsView = "root"
                            renderSettingsMenu()
                        }
                    }, 120)
                }
                settingsPanel.appendChild(row)
                i++
            }
        }

        renderSettingsMenu = renderSettingsMenu@{
            try {
                while (settingsPanel.firstChild != null) {
                    settingsPanel.removeChild(settingsPanel.firstChild!!)
                }
                val snap = if (isNullish(settingsSnapshot)) {
                    defaultSettingsSnapshot()
                } else {
                    normalizeSettingsSnapshot(settingsSnapshot)
                }
                when (settingsView) {
                    "root" -> {
                        appendSettingsHeader("Настройки", showBack = false)
                        appendRootRow("quality", sectionOf(snap, "quality"), always = true)
                        appendRootRow("sound", sectionOf(snap, "sound"), always = true)
                        appendRootRow("cc", sectionOf(snap, "cc"), always = true)
                        appendRootRow("skip", sectionOf(snap, "skip"))
                    }
                    else -> {
                        appendSettingsHeader(sectionTitle(settingsView), showBack = true)
                        appendOptionRows(settingsView, sectionOf(snap, settingsView))
                    }
                }
            } catch (t: Throwable) {
                consoleInfo("kinoshka-player: renderSettingsMenu failed: $t")
                try {
                    while (settingsPanel.firstChild != null) {
                        settingsPanel.removeChild(settingsPanel.firstChild!!)
                    }
                    val fallback = defaultSettingsSnapshot()
                    appendSettingsHeader("Настройки", showBack = false)
                    appendRootRow("quality", sectionOf(fallback, "quality"), always = true)
                    appendRootRow("sound", sectionOf(fallback, "sound"), always = true)
                    appendRootRow("cc", sectionOf(fallback, "cc"), always = true)
                } catch (t2: Throwable) {
                    consoleInfo("kinoshka-player: settings fallback failed: $t2")
                }
            }
        }

        fun setControlsVisible(visible: Boolean) {
            controlsVisible = visible
            chrome.style.opacity = if (visible) "1" else "0"
            // Children own hit-testing; chrome itself stays non-blocking so idle
            // auto-hide still works even if the cursor sits still over the video.
            chrome.style.cssSet("pointer-events", "none")
            val pe = if (visible) "auto" else "none"
            backBtn.style.cssSet("pointer-events", pe)
            fsBtn.style.cssSet("pointer-events", pe)
            settingsBtn.style.cssSet("pointer-events", pe)
            playBtn.style.cssSet("pointer-events", pe)
            seek.style.cssSet("pointer-events", pe)
            if (!visible) {
                closeSettingsMenu()
            } else if (settingsOpen) {
                settingsPanel.style.display = "block"
                settingsPanel.style.cssSet("pointer-events", "auto")
            }
            shield.style.cssSet("pointer-events", "auto")
            root.style.cursor = if (visible) "default" else "none"
            focusables.forEach { el ->
                el.tabIndex = if (visible) 0 else -1
            }
            if (!visible) {
                pointerOverControl = false
                // Keep focus inside the player document (root is focusable).
                // A bare blur() leaves the page with no active element; on macOS exiting
                // Fullscreen API can then hand OS focus to another app.
                try {
                    val active = document.activeElement as? HTMLElement
                    if (active != null && focusables.any { it === active }) {
                        root.focus()
                    }
                } catch (_: Throwable) {
                }
            }
        }

        fun scheduleHide() {
            hideTimerId?.let { window.clearTimeout(it) }
            hideTimerId = window.setTimeout({
                // Keep visible while hovering a control, scrubbing, or settings open.
                if (pointerOverControl || seeking || dragSeeking || settingsOpen) {
                    scheduleHide()
                    return@setTimeout
                }
                setControlsVisible(false)
            }, CONTROLS_HIDE_MS)
        }

        /** Show chrome and always (re)start auto-hide timer. */
        fun showControls() {
            setControlsVisible(true)
            scheduleHide()
        }

        fun openSettingsMenu() {
            showControls()
            injectBridgeIntoIframe(iframe)
            settingsSnapshot = readSettingsSnapshot()
            settingsOpen = true
            settingsView = "root"
            settingsPanel.style.display = "block"
            renderSettingsMenu()
            // Keep refreshing until Venom fills quality / sound / cc lists.
            var tries = 0
            fun refresh() {
                if (!settingsOpen || tries > 25) return
                tries++
                injectBridgeIntoIframe(iframe)
                val next = readSettingsSnapshot()
                settingsSnapshot = next
                if (settingsView == "root" || settingsView == "quality" ||
                    settingsView == "sound" || settingsView == "cc"
                ) {
                    renderSettingsMenu()
                }
                val qLen = jsArrayLength(next?.quality?.list)
                val sLen = jsArrayLength(next?.sound?.list)
                val cLen = jsArrayLength(next?.cc?.list)
                if (qLen == 0 && sLen == 0 && cLen == 0) {
                    window.setTimeout({ refresh() }, 300)
                }
            }
            window.setTimeout({ refresh() }, 200)
        }

        fun toggleSettingsMenu() {
            if (settingsOpen) closeSettingsMenu() else openSettingsMenu()
        }

        settingsBtn.onclick = {
            showControls()
            toggleSettingsMenu()
        }
        settingsPanel.addEventListener("mouseenter", {
            pointerOverControl = true
            showControls()
        })
        settingsPanel.addEventListener("mouseleave", {
            pointerOverControl = false
            if (!settingsOpen) scheduleHide()
        })

        fun focusAt(index: Int) {
            focusedIndex = index.coerceIn(0, focusables.lastIndex)
            showControls()
            try {
                focusables[focusedIndex].focus()
            } catch (_: Throwable) {
            }
        }

        /** Pull keyboard/OS focus back to this browser tab + player chrome. */
        fun reclaimBrowserFocus(preferControlIndex: Int = 1) {
            fun tick() {
                if (cleaningUp) return
                try {
                    window.focus()
                } catch (_: Throwable) {
                }
                try {
                    root.focus()
                } catch (_: Throwable) {
                }
                try {
                    showControls()
                    focusAt(preferControlIndex)
                } catch (_: Throwable) {
                }
            }
            // macOS often reassigns OS focus *after* fullscreenchange; retry a few frames.
            tick()
            listOf(16, 50, 120, 250, 400).forEach { ms ->
                window.setTimeout({ tick() }, ms)
            }
        }

        /** Esc that exits Fullscreen API must not also close the player / lose the tab. */
        var fullscreenExitGuardUntilMs = 0.0
        fun markExitedFullscreen() {
            val now = try {
                window.performance.now()
            } catch (_: Throwable) {
                js("Date.now()") as Double
            }
            fullscreenExitGuardUntilMs = now + 500.0
            reclaimBrowserFocus(preferControlIndex = 1)
        }
        fun withinFullscreenExitGuard(): Boolean {
            val now = try {
                window.performance.now()
            } catch (_: Throwable) {
                js("Date.now()") as Double
            }
            return now < fullscreenExitGuardUntilMs
        }

        fun moveFocus(delta: Int) {
            focusAt(focusedIndex + delta)
        }

        playBtn.onclick = { event ->
            event.preventDefault()
            event.stopPropagation()
            try {
                event.asDynamic().stopImmediatePropagation()
            } catch (_: Throwable) {
            }
            showControls()
            togglePlay()
        }

        fun fractionFromSeekPointer(clientX: Double): Double {
            val rect = seekTrack.getBoundingClientRect()
            if (rect.width <= 0) return seekFraction
            return ((clientX - rect.left) / rect.width).coerceIn(0.0, 1.0)
        }

        fun previewSeekFraction(fraction: Double) {
            seeking = true
            seekFraction = fraction.coerceIn(0.0, 1.0)
            if (durationSec > 0) {
                currentSec = durationSec * seekFraction
            }
            timeCur.textContent = formatTime(currentSec)
            seekFill.style.width = "${(seekFraction * 100.0)}%"
        }

        // Click + drag scrub (mouse): mousedown/up only — avoid double-seek from click+up.
        val onSeekMouseDown: (Event) -> Unit = { event ->
            dragSeeking = true
            showControls()
            focusAt(2)
            val mouse = event.asDynamic()
            val x = jsDouble(mouse.clientX)
            if (x != null) previewSeekFraction(fractionFromSeekPointer(x))
            event.preventDefault()
        }
        val onWindowMouseMove: (Event) -> Unit = onWindowMouseMove@{ event ->
            if (!dragSeeking) return@onWindowMouseMove
            val mouse = event.asDynamic()
            val x = jsDouble(mouse.clientX) ?: return@onWindowMouseMove
            previewSeekFraction(fractionFromSeekPointer(x))
        }
        val onWindowMouseUp: (Event) -> Unit = onWindowMouseUp@{
            if (!dragSeeking) return@onWindowMouseUp
            dragSeeking = false
            seekToFraction(seekFraction)
        }
        seek.addEventListener("mousedown", onSeekMouseDown)
        window.addEventListener("mousemove", onWindowMouseMove)
        window.addEventListener("mouseup", onWindowMouseUp)

        seek.addEventListener("keydown", { event ->
            val keyEvent = event as? KeyboardEvent ?: return@addEventListener
            when (keyEvent.key) {
                "ArrowLeft", "ArrowRight" -> {
                    keyEvent.preventDefault()
                    keyEvent.stopPropagation()
                    showControls()
                    seekBy(if (keyEvent.key == "ArrowRight") SEEK_STEP_SEC else -SEEK_STEP_SEC)
                }
                "Home" -> {
                    keyEvent.preventDefault()
                    keyEvent.stopPropagation()
                    showControls()
                    seekToSeconds(0.0)
                }
                "End" -> {
                    keyEvent.preventDefault()
                    keyEvent.stopPropagation()
                    showControls()
                    if (durationSec > 0) seekToSeconds(durationSec)
                }
                "Enter", " " -> {
                    keyEvent.preventDefault()
                    keyEvent.stopPropagation()
                    showControls()
                    togglePlay()
                }
            }
        })

        focusables.forEachIndexed { index, el ->
            el.addEventListener("focus", {
                focusedIndex = index
                showControls()
            })
            el.addEventListener("mouseenter", {
                pointerOverControl = true
                showControls()
            })
            el.addEventListener("mouseleave", {
                pointerOverControl = false
                scheduleHide()
            })
        }

        shield.onmousemove = { showControls() }
        chrome.onmousemove = { showControls() }
        shield.onclick = { event ->
            event.preventDefault()
            event.stopPropagation()
            showControls()
            focusAt(1) // play
            togglePlay()
        }
        showControls()
        window.setTimeout({ focusAt(1) }, 300)

        val onFullscreenChange: (Event) -> Unit = {
            val full = isFullscreen()
            updateFullscreenIcon()
            if (!full) {
                // Esc / browser UI / our button all go through fullscreenchange.
                markExitedFullscreen()
            }
        }
        document.addEventListener("fullscreenchange", onFullscreenChange)
        document.addEventListener("webkitfullscreenchange", onFullscreenChange)
        document.addEventListener("mozfullscreenchange", onFullscreenChange)

        val onKeyUp: (Event) -> Unit = onKeyUp@{ event ->
            val keyEvent = event as? KeyboardEvent ?: return@onKeyUp
            if (keyEvent.key != "Escape") return@onKeyUp
            // After Esc exits FS, keyup is a reliable second chance to reclaim focus.
            if (withinFullscreenExitGuard()) {
                reclaimBrowserFocus(preferControlIndex = 1)
            }
        }
        window.addEventListener("keyup", onKeyUp, true)

        val onKeyDown: (Event) -> Unit = onKeyDown@{ event ->
            val keyEvent = event as? KeyboardEvent ?: return@onKeyDown
            val key = keyEvent.key
            val code = keyEvent.code
            val active = document.activeElement

            fun consumeEscapeOrBack() {
                keyEvent.preventDefault()
                keyEvent.stopPropagation()
                try {
                    keyEvent.asDynamic().stopImmediatePropagation()
                } catch (_: Throwable) {
                }
                when {
                    settingsOpen && settingsView != "root" -> {
                        settingsView = "root"
                        renderSettingsMenu()
                    }
                    settingsOpen -> closeSettingsMenu()
                    isFullscreen() -> {
                        // Browser may already be exiting FS on Escape; still reclaim.
                        exitFullscreen()
                        markExitedFullscreen()
                    }
                    withinFullscreenExitGuard() -> {
                        // Same Esc that just left fullscreen — do not close the player.
                        reclaimBrowserFocus(preferControlIndex = 1)
                    }
                    else -> closePlayer()
                }
            }

            // Escape / browser-back: FS first, then close player. Never let root OnBackPressed
            // also fire on the same key (that was popping details underneath).
            if (key == "Escape" || key == "Backspace" || key == "GoBack" || code == "BrowserBack") {
                consumeEscapeOrBack()
                return@onKeyDown
            }

            // Any key / remote press wakes chrome and resets auto-hide (TV has no mouse).
            val wasHidden = !controlsVisible
            showControls()
            if (wasHidden) {
                // First press only reveals chrome + focuses play; second press acts.
                if (key == "ArrowUp" || key == "ArrowDown" || key == "ArrowLeft" || key == "ArrowRight" ||
                    key == "Enter" || key == "NumpadEnter" || key == " " ||
                    key == "MediaPlayPause" || code == "MediaPlayPause"
                ) {
                    keyEvent.preventDefault()
                    focusAt(1)
                    if (key == " " || key == "MediaPlayPause" || code == "MediaPlayPause") {
                        togglePlay()
                    }
                    return@onKeyDown
                }
            }

            when {
                key == " " || key == "k" || key == "K" ||
                    key == "MediaPlayPause" || code == "MediaPlayPause" -> {
                    keyEvent.preventDefault()
                    focusAt(1)
                    togglePlay()
                }
                key == "MediaPlay" || code == "MediaPlay" -> {
                    keyEvent.preventDefault()
                    if (!isPlaying) togglePlay()
                }
                key == "MediaPause" || code == "MediaPause" -> {
                    keyEvent.preventDefault()
                    if (isPlaying) togglePlay()
                }
                key == "MediaFastForward" || code == "MediaFastForward" -> {
                    keyEvent.preventDefault()
                    focusAt(2)
                    seekBy(SEEK_STEP_SEC)
                }
                key == "MediaRewind" || code == "MediaRewind" -> {
                    keyEvent.preventDefault()
                    focusAt(2)
                    seekBy(-SEEK_STEP_SEC)
                }
                // Spatial focus: corners [back][fs] / center play / bottom seek
                key == "ArrowRight" -> {
                    keyEvent.preventDefault()
                    when {
                        active === seek -> {
                            keyEvent.stopPropagation()
                            seekBy(SEEK_STEP_SEC)
                        }
                        active === backBtn -> focusAt(4) // fullscreen
                        active === playBtn -> focusAt(2) // seek
                        active === settingsBtn -> focusAt(4)
                        active === fsBtn -> { /* end */ }
                        else -> {
                            focusAt(2)
                            seekBy(SEEK_STEP_SEC)
                        }
                    }
                }
                key == "ArrowLeft" -> {
                    keyEvent.preventDefault()
                    when {
                        active === seek -> {
                            keyEvent.stopPropagation()
                            seekBy(-SEEK_STEP_SEC)
                        }
                        active === fsBtn -> focusAt(3) // settings
                        active === settingsBtn -> focusAt(0) // back
                        active === playBtn -> { /* stay */ }
                        active === backBtn -> { /* start */ }
                        else -> {
                            focusAt(2)
                            seekBy(-SEEK_STEP_SEC)
                        }
                    }
                }
                key == "ArrowDown" -> {
                    keyEvent.preventDefault()
                    when {
                        active === backBtn || active === fsBtn || active === settingsBtn || active === playBtn ->
                            focusAt(2)
                        else -> focusAt(2)
                    }
                }
                key == "ArrowUp" -> {
                    keyEvent.preventDefault()
                    when {
                        active === seek -> focusAt(1) // play
                        active === playBtn -> focusAt(0) // back
                        else -> focusAt(1)
                    }
                }
                key == "Enter" || key == "NumpadEnter" -> {
                    if (active === seek) {
                        keyEvent.preventDefault()
                        togglePlay()
                    } else if (active === settingsBtn) {
                        keyEvent.preventDefault()
                        toggleSettingsMenu()
                    } else if (active === fsBtn || active === backBtn || active === playBtn) {
                        // native click will fire for focused button
                    } else if (!chromeHasFocus()) {
                        keyEvent.preventDefault()
                        focusAt(1)
                        togglePlay()
                    }
                }
            }
        }
        // Capture phase so we win over Compose canvas handlers when possible.
        window.addEventListener("keydown", onKeyDown, true)

        val onMessage: (Event) -> Unit = onMessage@{ event ->
            val msg = event as? MessageEvent ?: return@onMessage
            val data = msg.data ?: return@onMessage
            // Ignore non-object payloads (ping strings etc.)
            if (jsTypeOf(data) != "object") return@onMessage
            val dyn = data.asDynamic()
            val eventName = jsString(dyn.event) ?: return@onMessage

            when (eventName) {
                "viewProgress" -> {
                    val time = jsDouble(dyn.time) ?: return@onMessage
                    val duration = jsDouble(dyn.duration) ?: return@onMessage
                    val paused = when (val p = dyn.paused) {
                        null, undefined -> null
                        else -> p == true
                    }
                    applyProgress(time, duration, paused)
                }
                "kinoshkaReady", "playerReady" -> {
                    setBridgeReady(null)
                    tryBindApp()
                }
                "startWatching" -> {
                    isPlaying = true
                    updatePlayIcon()
                    tryBindApp()
                }
                "ended" -> {
                    isPlaying = false
                    updatePlayIcon()
                }
            }
        }
        window.addEventListener("message", onMessage)

        var primedHtml: String? = null
        var usedSrcdocFallback = false

        fun onPlayerFrameLoaded() {
            if (cleaningUp) return
            val originOk = canAccessIframe(iframe)
            consoleInfo("kinoshka-player: iframe load, originOk=$originOk")
            if (!originOk) {
                val html = primedHtml
                if (!usedSrcdocFallback && html != null && looksLikePlayerEmbedHtml(html)) {
                    usedSrcdocFallback = true
                    consoleInfo("kinoshka-player: cross-origin after load — srcdoc fallback")
                    iframe.removeAttribute("src")
                    iframe.srcdoc = html
                    return
                }
            }
            choiceInstalled = false
            injectVenomHideCss(iframe)
            injectBridgeIntoIframe(iframe)
            tryBindApp()
            startAppPolling()
            startProgressPolling()
            startPlaybackChoice()
            fun keepBound() {
                if (cleaningUp) return
                if (canAccessIframe(iframe)) {
                    injectVenomHideCss(iframe)
                    injectBridgeIntoIframe(iframe)
                    tryBindApp()
                }
                window.setTimeout({ keepBound() }, 800)
            }
            window.setTimeout({ keepBound() }, 800)
        }

        // Load after helpers exist. Same-origin iframe → hide Venom + drive play/seek.
        iframe.onload = { onPlayerFrameLoaded() }
        consoleInfo("kinoshka-player: iframe.src=$src")
        whenEmbedProxyReady {
            if (cleaningUp) return@whenEmbedProxyReady
            seedEmbedHtmlForProxy(tobacoSrc, src) { html ->
                if (cleaningUp) return@seedEmbedHtmlForProxy
                primedHtml = html
                iframe.src = src
            }
        }
        startAppPolling()
        startProgressPolling()
        startPlaybackChoice()

        onDispose {
            cleaningUp = true
            releaseWakeLock()
            document.removeEventListener("visibilitychange", visibilityHandler)
            hideTimerId?.let { window.clearTimeout(it) }
            appPollTimerId?.let { window.clearTimeout(it) }
            progressTimerId?.let { window.clearInterval(it) }
            seekLockTimerId?.let { window.clearTimeout(it) }
            if (isFullscreen()) exitFullscreen()
            window.removeEventListener("keydown", onKeyDown, true)
            window.removeEventListener("keyup", onKeyUp, true)
            window.removeEventListener("message", onMessage)
            window.removeEventListener("mousemove", onWindowMouseMove)
            window.removeEventListener("mouseup", onWindowMouseUp)
            document.removeEventListener("fullscreenchange", onFullscreenChange)
            document.removeEventListener("webkitfullscreenchange", onFullscreenChange)
            document.removeEventListener("mozfullscreenchange", onFullscreenChange)
            shield.onmousemove = null
            shield.onclick = null
            chrome.onmousemove = null
            seek.removeEventListener("mousedown", onSeekMouseDown)
            root.remove()
        }
    }
}

/** Map tobaco/femd embed URLs onto same-origin proxy paths when available (webpack dev). */
private fun toSameOriginPlayerUrl(absoluteUrl: String): String {
    if (!shouldUseEmbedProxy()) return absoluteUrl
    val origin = window.location.origin
    return absoluteUrl
        .replace("https://api.tobaco.ws", "$origin/tobaco-embed")
        .replace("http://api.tobaco.ws", "$origin/tobaco-embed")
        .replace("https://api.femd.ws", "$origin/femd-embed")
        .replace("http://api.femd.ws", "$origin/femd-embed")
}

/**
 * Local webpack proxy, or prod SW at /kinoshka-embed-sw.js (browser IP, not VPS).
 * Same-origin iframe is required to hide Venom chrome and drive play/seek.
 */
private fun shouldUseEmbedProxy(): Boolean {
    val host = window.location.hostname
    if (host == "localhost" || host == "127.0.0.1" || host == "[::1]") return true
    if (host == "kinoshka.net" || host == "www.kinoshka.net") return true
    return try {
        window.asDynamic().__KINOSHKA_EMBED_PROXY == true
    } catch (_: Throwable) {
        false
    }
}

private fun whenEmbedProxyReady(onReady: () -> Unit) {
    var done = false
    fun once() {
        if (done) return
        done = true
        onReady()
    }
    val host = window.location.hostname
    val needsSw = host == "kinoshka.net" || host == "www.kinoshka.net"
    if (!needsSw) {
        once()
        return
    }
    try {
        val sw = window.navigator.asDynamic().serviceWorker
        if (sw == null) {
            consoleInfo("kinoshka-player: no serviceWorker — iframe may hit blocked nginx proxy")
            once()
            return
        }
        sw.register("/kinoshka-embed-sw.js?v=1.0.31").then {
            sw.ready.then {
                if (sw.controller != null) {
                    once()
                } else {
                    sw.addEventListener("controllerchange") { once() }
                    window.setTimeout({ once() }, 2000)
                }
                null
            }.catch { err: dynamic ->
                consoleInfo("kinoshka-player: SW ready failed: $err")
                once()
                null
            }
            null
        }.catch { err: dynamic ->
            consoleInfo("kinoshka-player: SW register failed: $err")
            once()
            null
        }
        window.setTimeout({ once() }, 3500)
    } catch (t: Throwable) {
        consoleInfo("kinoshka-player: SW setup failed: $t")
        once()
    }
}

private fun looksLikePlayerEmbedHtml(html: String): Boolean {
    if (html.length < 500) return false
    val lower = html.lowercase()
    if ("protonmail.com" in lower) return false
    if ("<html" !in lower && "<!doctype" !in lower) return false
    return "player" in lower || "venom" in lower || "filmot" in lower || "mms" in lower
}

private fun proxyPathOf(proxyUrl: String): String {
    return try {
        val parsed = js("new URL")(proxyUrl)
        val path = parsed.pathname?.toString() ?: ""
        val search = parsed.search?.toString() ?: ""
        path + search
    } catch (_: Throwable) {
        proxyUrl
    }
}

/** Page-fetch the real tobaco/femd HTML (user IP), then serve it from the SW at /tobaco-embed. */
private fun seedEmbedHtmlForProxy(absoluteUrl: String, proxyUrl: String, onDone: (String?) -> Unit) {
    var done = false
    fun once(html: String? = null) {
        if (done) return
        done = true
        onDone(html)
    }
    val host = window.location.hostname
    val useSw = host == "kinoshka.net" || host == "www.kinoshka.net"
    if (!useSw) {
        once(null)
        return
    }
    window.setTimeout({ once(null) }, 8000)
    try {
        val init = js("({ method: 'GET', mode: 'cors', credentials: 'omit', cache: 'no-store' })")
        window.asDynamic().fetch(absoluteUrl, init).then { response ->
            val ok = response.asDynamic().ok == true
            response.asDynamic().text().then { textDyn ->
                val html = textDyn as? String ?: textDyn.toString()
                if (!ok || !looksLikePlayerEmbedHtml(html)) {
                    consoleInfo("kinoshka-player: origin fetch unusable, SW will try upstream")
                    once(null)
                    return@then null
                }
                primeServiceWorkerHtml(proxyPathOf(proxyUrl), html) { once(html) }
                null
            }.catch { err: dynamic ->
                consoleInfo("kinoshka-player: origin fetch body failed: $err")
                once(null)
                null
            }
            null
        }.catch { err: dynamic ->
            consoleInfo("kinoshka-player: origin fetch failed: $err")
            once(null)
            null
        }
    } catch (t: Throwable) {
        consoleInfo("kinoshka-player: origin fetch setup failed: $t")
        once(null)
    }
}

private fun primeServiceWorkerHtml(path: String, html: String, onDone: () -> Unit) {
    var done = false
    fun once() {
        if (done) return
        done = true
        onDone()
    }
    window.setTimeout({ once() }, 800)
    try {
        val controller = window.navigator.asDynamic().serviceWorker?.controller
        if (controller == null) {
            consoleInfo("kinoshka-player: no SW controller to prime")
            once()
            return
        }
        val channel = js("new MessageChannel()")
        channel.port1.onmessage = { once() }
        val payload = js("{}")
        payload.type = "kinoshka-embed-html"
        payload.path = path
        payload.html = html
        js("controller.postMessage(payload, [channel.port2])")
        consoleInfo("kinoshka-player: primed SW html (${html.length} chars) path=$path")
    } catch (t: Throwable) {
        consoleInfo("kinoshka-player: SW prime failed: $t")
        once()
    }
}

private fun canAccessIframe(iframe: HTMLIFrameElement): Boolean {
    return try {
        iframe.contentDocument != null || iframe.contentWindow?.asDynamic()?.document != null
    } catch (_: Throwable) {
        false
    }
}

/** Force currentTime via the element setter so HLS/Venom interceptors handle the seek. */
private fun forceVideoCurrentTime(video: dynamic, seconds: Double) {
    try {
        video.currentTime = seconds
    } catch (_: Throwable) {
    }
}

private fun injectVenomHideCss(iframe: HTMLIFrameElement) {
    try {
        val doc = iframe.contentDocument ?: return
        var style = doc.getElementById("kinoshka-hide-venom")
        if (style == null) {
            style = doc.createElement("style")
            style.id = "kinoshka-hide-venom"
            (doc.head ?: doc.documentElement ?: doc.body)?.appendChild(style)
        }
        style.asDynamic().textContent = VENOM_HIDE_CSS
        try {
            doc.documentElement?.classList?.add("kinoshka-hide-venom")
            doc.body?.classList?.add("kinoshka-hide-venom")
        } catch (_: Throwable) {
        }
        try {
            val videos = doc.querySelectorAll("video")
            val len = jsArrayLength(videos)
            var i = 0
            while (i < len) {
                val v = videos.item(i)?.asDynamic()
                if (v != null) {
                    v.controls = false
                    try {
                        v.removeAttribute("controls")
                    } catch (_: Throwable) {
                    }
                }
                i++
            }
        } catch (_: Throwable) {
        }
    } catch (t: Throwable) {
        window.asDynamic().console.info("kinoshka-player: hide venom css failed: $t")
    }
}

private fun injectBridgeIntoIframe(iframe: HTMLIFrameElement) {
    try {
        val doc = iframe.contentDocument ?: return
        injectVenomHideCss(iframe)
        val win = iframe.contentWindow?.asDynamic()
        // Always neutralize Object.prototype.ctrl trap left by older builds.
        if (doc.getElementById("kinoshka-cleanup") == null) {
            val cleanup = doc.createElement("script")
            cleanup.id = "kinoshka-cleanup"
            cleanup.asDynamic().text =
                """
                try {
                  if (Object.prototype.__ksCtrlTrap) {
                    delete Object.prototype.ctrl;
                    delete Object.prototype.__ksCtrlTrap;
                  }
                  var early = document.getElementById('kinoshka-early');
                  if (early && early.parentNode) early.parentNode.removeChild(early);
                } catch (e) {}
                """.trimIndent()
            (doc.head ?: doc.documentElement ?: doc.body)?.appendChild(cleanup)
        }
        val existing = doc.getElementById("kinoshka-bridge")
        val hasApi = try {
            val ver = jsDouble(win?.__kinoshka?.bridgeVersion) ?: 0.0
            win != null &&
                ver >= 28.0 &&
                jsTypeOf(win.__kinoshka?.setPlaying) == "function" &&
                jsTypeOf(win.__kinoshka?.seek) == "function"
        } catch (_: Throwable) {
            false
        }
        if (hasApi) return
        existing?.parentNode?.removeChild(existing)
        try {
            if (win != null) {
                win.__kinoshkaBridge = 0
                win.__kinoshkaBound = 0
            }
        } catch (_: Throwable) {
        }
        try {
            doc.getElementById("kinoshka-early")?.let { it.parentNode?.removeChild(it) }
        } catch (_: Throwable) {
        }
        val script = doc.createElement("script")
        script.id = "kinoshka-bridge"
        script.asDynamic().text = BRIDGE_SCRIPT_BODY
        (doc.head ?: doc.documentElement ?: doc.body)?.appendChild(script)
        window.asDynamic().console.info("kinoshka-player: bridge injected into iframe")
    } catch (t: Throwable) {
        window.asDynamic().console.info("kinoshka-player: bridge inject failed (cross-origin?): $t")
    }
}

/** Kotlin/JS: JS strings/numbers often fail `as? String` / `as? Number`. */
private fun jsString(value: dynamic): String? {
    if (js("value === null || value === undefined").unsafeCast<Boolean>()) return null
    return when (jsTypeOf(value)) {
        "string" -> value.unsafeCast<String>()
        "number", "boolean" -> value.toString()
        else -> null
    }
}

private fun jsDouble(value: dynamic): Double? {
    if (js("value === null || value === undefined").unsafeCast<Boolean>()) return null
    return when (jsTypeOf(value)) {
        "number" -> value.unsafeCast<Number>().toDouble()
        "string" -> value.unsafeCast<String>().toDoubleOrNull()
        else -> null
    }
}

private fun jsArrayLength(value: dynamic): Int {
    if (js("value === null || value === undefined").unsafeCast<Boolean>()) return 0
    val len = js("value.length")
    return jsDouble(len)?.toInt()?.coerceAtLeast(0) ?: 0
}

private fun jsArrayItem(value: dynamic, index: Int): dynamic {
    if (js("value === null || value === undefined").unsafeCast<Boolean>()) return null
    return js("value[index]")
}

private fun jsTypeOf(value: dynamic): String {
    return js("typeof value").unsafeCast<String>()
}

private fun consoleInfo(message: String) {
    console.info(message)
}

private fun org.w3c.dom.css.CSSStyleDeclaration.cssSet(name: String, value: String) {
    asDynamic().setProperty(name, value)
}

private val console: dynamic get() = window.asDynamic().console

private const val FOCUS_CSS = """
#kinoshka-player-root .kinoshka-corner {
  transition: background 120ms ease, border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease;
}
#kinoshka-player-root .kinoshka-corner:hover {
  border-color: #4DA3FF !important;
  box-shadow: 0 0 0 4px rgba(77,163,255,0.55);
  background: rgba(77,163,255,0.28) !important;
  transform: scale(1.04);
}
#kinoshka-player-root .kinoshka-play {
  outline: none;
}
#kinoshka-player-root .kinoshka-play:hover {
  border-color: #4DA3FF !important;
  border-width: 3px !important;
  background: #4DA3FF !important;
  box-shadow: 0 0 0 4px rgba(77,163,255,0.55);
  transform: scale(1.06);
}
#kinoshka-player-root .kinoshka-seek {
  outline: none;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
#kinoshka-player-root .kinoshka-seek:hover {
  border-color: #4DA3FF !important;
  box-shadow: 0 0 0 4px rgba(77,163,255,0.55);
}
#kinoshka-player-root .kinoshka-seek:hover #kinoshka-player-seek-track {
  height: 8px;
  background: rgba(255,255,255,0.95);
}
#kinoshka-player-root .kinoshka-corner:focus,
#kinoshka-player-root .kinoshka-play:focus,
#kinoshka-player-root .kinoshka-seek:focus {
  outline: none;
}
#kinoshka-player-play svg,
#kinoshka-player-back svg,
#kinoshka-player-fs svg,
#kinoshka-player-settings svg {
  display: block;
  pointer-events: none;
}
"""

/** Shared hide CSS + seek/play bridge (Android/desktop parity). */
private val VENOM_HIDE_CSS: String
    get() = PlayerEmbedScripts.VENOM_HIDE_CSS + WEB_VIDEO_VISIBLE_CSS

private val BRIDGE_SCRIPT_BODY: String get() = PlayerEmbedScripts.BRIDGE_SCRIPT_BODY

/** Keep the picture visible: Venom wraps <video> in overlay/center nodes that the hide CSS collapses. */
private const val WEB_VIDEO_VISIBLE_CSS = """
#player {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  width: 100% !important;
  height: 100% !important;
  overflow: visible !important;
}
#player video {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
  pointer-events: none !important;
  background: #000 !important;
  position: relative !important;
  z-index: 2 !important;
}
#player *:has(> video),
#player *:has(video) {
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  width: 100% !important;
  height: 100% !important;
  max-width: none !important;
  max-height: none !important;
  overflow: visible !important;
  pointer-events: none !important;
  background: transparent !important;
}
"""


private const val BACK_ICON_SVG =
    """<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
<path d="M3.514 5H12.027C16.983 5 21 8.806 21 13.5C21 18.194 16.983 22 12.027 22H4.029" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M5.571 2L3 5L5.571 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>"""

private const val ENTER_FULLSCREEN_SVG =
    """<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 96 960 960" width="26" height="26" aria-hidden="true">
<path fill="currentColor" d="M230 856q-12.75 0-21.375-8.625T200 826V693q0-12.75 8.675-21.375 8.676-8.625 21.5-8.625 12.825 0 21.325 8.625T260 693v103h103q12.75 0 21.375 8.675 8.625 8.676 8.625 21.5 0 12.825-8.625 21.325T363 856H230Zm-.175-367Q217 489 208.5 480.375T200 459V326q0-12.75 8.625-21.375T230 296h133q12.75 0 21.375 8.675 8.625 8.676 8.625 21.5 0 12.825-8.625 21.325T363 356H260v103q0 12.75-8.675 21.375-8.676 8.625-21.5 8.625ZM597 856q-12.75 0-21.375-8.675-8.625-8.676-8.625-21.5 0-12.825 8.625-21.325T597 796h103V693q0-12.75 8.675-21.375 8.676-8.625 21.5-8.625 12.825 0 21.325 8.625T760 693v133q0 12.75-8.625 21.375T730 856H597Zm132.825-367Q717 489 708.5 480.375T700 459V356H597q-12.75 0-21.375-8.675-8.625-8.676-8.625-21.5 0-12.825 8.625-21.325T597 296h133q12.75 0 21.375 8.625T760 326v133q0 12.75-8.675 21.375-8.676 8.625-21.5 8.625Z"/>
</svg>"""

private const val EXIT_FULLSCREEN_SVG =
    """<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 96 960 960" width="26" height="26" aria-hidden="true">
<path fill="currentColor" d="M362.825 856Q350 856 341.5 847.375T333 826V723H230q-12.75 0-21.375-8.675-8.625-8.676-8.625-21.5 0-12.825 8.625-21.325T230 663h133q12.75 0 21.375 8.625T393 693v133q0 12.75-8.675 21.375-8.676 8.625-21.5 8.625ZM230 489q-12.75 0-21.375-8.675-8.625-8.676-8.625-21.5 0-12.825 8.625-21.325T230 429h103V326q0-12.75 8.675-21.375 8.676-8.625 21.5-8.625 12.825 0 21.325 8.625T393 326v133q0 12.75-8.625 21.375T363 489H230Zm366.825 367Q584 856 575.5 847.375T567 826V693q0-12.75 8.625-21.375T597 663h133q12.75 0 21.375 8.675 8.625 8.676 8.625 21.5 0 12.825-8.625 21.325T730 723H627v103q0 12.75-8.675 21.375-8.676 8.625-21.5 8.625ZM597 489q-12.75 0-21.375-8.625T567 459V326q0-12.75 8.675-21.375 8.676-8.625 21.5-8.625 12.825 0 21.325 8.625T627 326v103h103q12.75 0 21.375 8.675 8.625 8.676 8.625 21.5 0 12.825-8.625 21.325T730 489H597Z"/>
</svg>"""

private const val SETTINGS_ICON_SVG =
    """<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" aria-hidden="true">
<path d="M12 15.5A3.5 3.5 0 1 0 12 8.5a3.5 3.5 0 0 0 0 7Z" stroke="currentColor" stroke-width="2"/>
<path d="M19.4 13.5a7.6 7.6 0 0 0 .05-1.5 7.6 7.6 0 0 0-.05-1.5l2.05-1.6-2-3.46-2.45 1a7.3 7.3 0 0 0-2.6-1.5L14 2h-4l-.4 2.94a7.3 7.3 0 0 0-2.6 1.5l-2.45-1-2 3.46L4.6 10.5a7.6 7.6 0 0 0-.05 1.5 7.6 7.6 0 0 0 .05 1.5L2.55 15.1l2 3.46 2.45-1a7.3 7.3 0 0 0 2.6 1.5L10 22h4l.4-2.94a7.3 7.3 0 0 0 2.6-1.5l2.45 1 2-3.46-2.05-1.6Z" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/>
</svg>"""

private const val PLAY_ICON_SVG =
    """<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="36" height="36" aria-hidden="true">
<path fill="currentColor" d="M8 5v14l11-7z"/>
</svg>"""

private const val PAUSE_ICON_SVG =
    """<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="36" height="36" aria-hidden="true">
<path fill="currentColor" d="M6 5h4v14H6zm8 0h4v14h-4z"/>
</svg>"""
